import { IconAdd, IconCheckDefaultCircle, IconErrorCircle, IconNew, IconViewDetails } from "@mzc-cloudplex/icons";
import { Box, Grid, IconButton, Stack, Typography, Skeleton } from "@mzc-pdc/ui";
import { useEffect, useState } from "react";
import { ImagePreviewButton, StyledAssetTypeThumbnail } from "./asset-thumbnail.styled";
import {
    ImageDefaultAudioDark,
    ImageDefaultAudioSmallDark,
    ImageDefaultFileDark,
    ImageDefaultFileSmallDark,
    ImageDefaultImageDark,
    ImageDefaultImageSmallDark,
    ImageDefaultTextDark,
    ImageDefaultTextSmallDark,
    ImageDefaultVideoDark,
    ImageDefaultVideoSmallDark,
} from "@mzc-cloudplex/icons";
import theme from "@styles/theme";
import {
    ARCHIVED_ASSET_STATUS,
    ASSET_PREVIEW_STATUS,
    ASSET_THUMBNAIL_CARD_CONTAINER_SIZE,
    ASSET_THUMBNAIL_TABLE_CONTAINER_SIZE,
    MEDIA_TYPES,
} from "@constants";
import useThumbnailCrop from "@hooks/useThumbnailCrop";
import Tooltip from "@components_v2/Tooltip/tooltip";
import { useTranslation } from "react-i18next";
import { AssetStatusArchived } from "@routes/asset/AssetStatus/AssetStatusArchived";
import countDaysBetweentDates from "@cores/countDaysBetweentDates";
import ImagePreviewV2 from "@components/modals/image-preview-v2";

const AssetThumbnail = ({
    variant,
    minWidth = 40,
    width = 40,
    height = 40,
    src,
    margin,
    disableHover = true,
    sx = {},
    thumbnailCrop,
    previewStatus = ASSET_PREVIEW_STATUS.COMPLETE,
    errorIconProps = { position: "left", size: 16 },
    showIcons = false,
    asset,
    ...restProps
}) => {
    const { t } = useTranslation();
    const { state, mediaType, status, archiveStatus, createdAt, created, isPreferred } = asset;

    const { previewContainerStyle, previewImageStyle } = useThumbnailCrop({
        thumbnailCrop,
        variant,
        cardContainerSize: ASSET_THUMBNAIL_CARD_CONTAINER_SIZE,
        tableContainerSize: ASSET_THUMBNAIL_TABLE_CONTAINER_SIZE,
    });

    const [imageStatus, setImageStatus] = useState("loading");
    const [isPreviewOpen, setIsPreviewOpen] = useState(false);

    const iconWidth = variant === "table" ? 28 : 46;
    const iconHeight = variant === "table" ? 28 : 56;

    useEffect(() => {
        if (!src) {
            setImageStatus("error");
            return;
        }

        setImageStatus("loading");

        const img = new Image();
        img.onload = () => {
            setImageStatus("success");
        };
        img.onerror = () => {
            setImageStatus("error");
        };
        img.src = src;
    }, [src]);

    const handlePreviewOpen = () => {
        setIsPreviewOpen(true);
    };

    const handlePreviewClose = () => {
        setIsPreviewOpen(false);
    };

    const handlePreviewClick = (event) => {
        event.stopPropagation();
        handlePreviewOpen();
    };

    const PreviewErrorIcon = () => {
        const errorIconSize = errorIconProps.size >= 24 ? "small" : "xsmall";
        if (errorIconProps.position === "left")
            return (
                <Tooltip title={t(`common::msg::Failed to load preview image. Please again.`)} maxWidth={201}>
                    <IconButton
                        size={"xsmall"}
                        circled
                        sx={{ position: "absolute", top: 0, left: 0 }}
                        onClick={() => {}}
                    >
                        <IconErrorCircle size={errorIconProps.size ?? 16} color={"error"} />
                    </IconButton>
                </Tooltip>
            );
        return (
            <Tooltip title={t(`common::msg::Failed to load preview image. Please again.`)} maxWidth={201}>
                <IconButton
                    size={errorIconSize}
                    circled
                    sx={{
                        position: "absolute",
                        top: `calc((100% - ${iconHeight}px)/2 - ${errorIconSize === "small" ? "12px" : "8px"})`,
                        right: `calc((100% - ${iconWidth}px)/2 - ${errorIconSize === "small" ? "12px" : "8px"})`,
                    }}
                    onClick={() => {}}
                >
                    <IconErrorCircle size={errorIconProps.size ?? 24} color={"error"} />
                </IconButton>
            </Tooltip>
        );
    };

    const DefaultIcon = ({ children }) => {
        let Component = null;
        if (variant === "table") {
            if (mediaType === MEDIA_TYPES.VIDEO) {
                Component = ImageDefaultVideoSmallDark;
            } else if (["MUSIC", MEDIA_TYPES.AUDIO].includes(mediaType)) {
                Component = ImageDefaultAudioSmallDark;
            } else if ([MEDIA_TYPES.TEXT, MEDIA_TYPES.CAPTION].includes(mediaType)) {
                Component = ImageDefaultTextSmallDark;
            } else if ([MEDIA_TYPES.IMAGE, "PHOTO", MEDIA_TYPES.PEOPLE].includes(mediaType)) {
                Component = ImageDefaultImageSmallDark;
            } else if (mediaType === MEDIA_TYPES.FILE) {
                Component = ImageDefaultFileSmallDark;
            } else return null;
        } else {
            if (mediaType === MEDIA_TYPES.VIDEO) {
                Component = ImageDefaultVideoDark;
            } else if (["MUSIC", MEDIA_TYPES.AUDIO].includes(mediaType)) {
                Component = ImageDefaultAudioDark;
            } else if ([MEDIA_TYPES.TEXT, MEDIA_TYPES.CAPTION].includes(mediaType)) {
                Component = ImageDefaultTextDark;
            } else if ([MEDIA_TYPES.IMAGE, "PHOTO", MEDIA_TYPES.PEOPLE].includes(mediaType)) {
                Component = ImageDefaultImageDark;
            } else if (mediaType === MEDIA_TYPES.FILE) {
                Component = ImageDefaultFileDark;
            } else return null;
        }

        return (
            <Box sx={{ position: "static" }}>
                <Component width={iconWidth} height={iconHeight} />
            </Box>
        );
    };

    const PreviewProgressIcon = () => {
        if (variant === "table")
            return (
                <Tooltip title={t(`common::msg::Preview image Creating...`)} maxWidth={110}>
                    <button>
                        <DefaultIcon />
                    </button>
                </Tooltip>
            );
        else
            return (
                <Stack direction={"column"} gap={theme.spacing(1)} justifyContent={"center"} alignItems={"center"}>
                    <DefaultIcon />
                    <Box
                        sx={{ backgroundColor: theme.palette.greySecondary[600] }}
                        px={theme.spacing(1.5)}
                        py={theme.spacing(0.25)}
                    >
                        <Typography color={theme.palette.common.white}>
                            {t(`common::msg::Preview image Creating...`)}
                        </Typography>
                    </Box>
                </Stack>
            );
    };

    const AssetDraftIcon = () => {
        if (variant === "table")
            return (
                <Tooltip title={t(`common::label::Creating asset...`)} maxWidth={110}>
                    <button>
                        <DefaultIcon />
                    </button>
                </Tooltip>
            );
        else
            return (
                <Stack direction={"column"} gap={theme.spacing(1)} justifyContent={"center"} alignItems={"center"}>
                    <DefaultIcon />
                    <Box
                        sx={{ backgroundColor: theme.palette.greySecondary[600] }}
                        px={theme.spacing(1.5)}
                        py={theme.spacing(0.25)}
                    >
                        <Typography color={theme.palette.common.white}>
                            {t(`common::label::Creating asset...`)}
                        </Typography>
                    </Box>
                </Stack>
            );
    };

    const DefaultIconByPreviewStatus = () => {
        switch (previewStatus) {
            case ASSET_PREVIEW_STATUS.IN_PROGRESS:
                return <PreviewProgressIcon />;
            case ASSET_PREVIEW_STATUS.ERROR:
                return (
                    <>
                        <DefaultIcon />
                        <PreviewErrorIcon />
                    </>
                );
            case ASSET_PREVIEW_STATUS.NOT_SUPPORTED:
                return <DefaultIcon />;
            default:
                return <DefaultIcon />;
        }
    };

    const renderMainContent = () => {
        if (state === "DRAFT") {
            return (
                <Box
                    sx={{
                        width: `100%`,
                        height: `100%`,
                        display: "flex",
                        position: "static",
                        alignItems: "center",
                        justifyContent: "center",
                        borderRadius: "2px",
                    }}
                >
                    <AssetDraftIcon />
                </Box>
            );
        }

        if (!src) {
            if (variant === "table") {
                return (
                    <>
                        {mediaType === MEDIA_TYPES.VIDEO && <ImageDefaultVideoSmallDark width={28} height={28} />}
                        {mediaType === MEDIA_TYPES.AUDIO && <ImageDefaultAudioSmallDark width={28} height={28} />}
                        {mediaType === MEDIA_TYPES.FILE && <ImageDefaultFileSmallDark width={28} height={28} />}
                        {mediaType === MEDIA_TYPES.IMAGE && <ImageDefaultImageSmallDark width={28} height={28} />}
                        {mediaType === MEDIA_TYPES.TEXT && <ImageDefaultTextSmallDark width={28} height={28} />}
                        {mediaType === MEDIA_TYPES.CAPTION && <ImageDefaultTextSmallDark width={28} height={28} />}
                    </>
                );
            } else {
                return (
                    <>
                        {mediaType === MEDIA_TYPES.VIDEO && <ImageDefaultVideoDark width={45} height={60} />}
                        {mediaType === MEDIA_TYPES.AUDIO && <ImageDefaultAudioDark width={47} height={60} />}
                        {mediaType === MEDIA_TYPES.FILE && <ImageDefaultFileDark width={47} height={60} />}
                        {mediaType === MEDIA_TYPES.IMAGE && <ImageDefaultImageDark width={47} height={60} />}
                        {mediaType === MEDIA_TYPES.TEXT && <ImageDefaultTextDark width={47} height={60} />}
                        {mediaType === MEDIA_TYPES.CAPTION && <ImageDefaultTextDark width={47} height={60} />}
                    </>
                );
            }
        }

        if (!!previewStatus && previewStatus !== ASSET_PREVIEW_STATUS.COMPLETE) {
            return (
                <Box
                    sx={{
                        width: `100%`,
                        height: `100%`,
                        display: "flex",
                        position: "static",
                        alignItems: "center",
                        justifyContent: "center",
                        borderRadius: "2px",
                    }}
                >
                    {["IMAGE", "FILE"].includes(mediaType) ? <DefaultIconByPreviewStatus /> : <DefaultIcon />}
                </Box>
            );
        }

        if (imageStatus === "loading") {
            return <></>;
        }

        if (imageStatus === "success") {
            return (
                <>
                    <Box sx={previewContainerStyle}>
                        <img src={src} alt="" draggable={false} />
                    </Box>
                    {!disableHover && (
                        <ImagePreviewButton
                            className={"btn-preview"}
                            style={{
                                width: variant === "card" ? "100px" : "32px",
                                height: variant === "card" ? "100px" : "32px",
                            }}
                            onClick={handlePreviewClick}
                        >
                            <Box
                                sx={{
                                    color: theme.palette.constant.white,
                                    backgroundColor: theme.palette.etc.gray500,
                                }}
                            >
                                <IconViewDetails size={20} />
                            </Box>
                            {variant === "card" && (
                                <Typography color={theme.palette.common.white}>
                                    {t(`common::label::Image`)} {t(`common::label::Zoom in`)}
                                </Typography>
                            )}
                        </ImagePreviewButton>
                    )}
                </>
            );
        }

        if (variant === "table") {
            return (
                <>
                    {mediaType === MEDIA_TYPES.VIDEO && <ImageDefaultVideoSmallDark width={28} height={28} />}
                    {mediaType === MEDIA_TYPES.AUDIO && <ImageDefaultAudioSmallDark width={28} height={28} />}
                    {mediaType === MEDIA_TYPES.FILE && <ImageDefaultFileSmallDark width={28} height={28} />}
                    {mediaType === MEDIA_TYPES.IMAGE && <ImageDefaultImageSmallDark width={28} height={28} />}
                    {mediaType === MEDIA_TYPES.TEXT && <ImageDefaultTextSmallDark width={28} height={28} />}
                    {mediaType === MEDIA_TYPES.CAPTION && <ImageDefaultTextSmallDark width={28} height={28} />}
                </>
            );
        } else {
            return (
                <>
                    {mediaType === MEDIA_TYPES.VIDEO && <ImageDefaultVideoDark width={45} height={60} />}
                    {mediaType === MEDIA_TYPES.AUDIO && <ImageDefaultAudioDark width={47} height={60} />}
                    {mediaType === MEDIA_TYPES.FILE && <ImageDefaultFileDark width={47} height={60} />}
                    {mediaType === MEDIA_TYPES.IMAGE && <ImageDefaultImageDark width={47} height={60} />}
                    {mediaType === MEDIA_TYPES.TEXT && <ImageDefaultTextDark width={47} height={60} />}
                    {mediaType === MEDIA_TYPES.CAPTION && <ImageDefaultTextDark width={47} height={60} />}
                </>
            );
        }
    };

    return (
        <StyledAssetTypeThumbnail
            className={"asset-thumbnail"}
            style={{
                minWidth,
                width,
                height,
                margin,
                ...sx,
            }}
            variant={variant}
            status={status}
            previewImageStyle={previewImageStyle}
            showIcons={showIcons}
            {...restProps}
        >
            {isPreferred && (
                <Grid
                    item
                    sx={{
                        position: "absolute",
                        left: variant === "card" ? "8px" : "-4px",
                        top: variant === "card" ? "8px" : "-4px",
                        justifyContent: "center",
                        alignItems: "center",
                        display: "flex",
                        backgroundColor: theme.palette.common.white,
                        borderRadius: "50%",
                        width: "13px",
                        height: "13px",
                        zIndex: 1,
                    }}
                >
                    <IconCheckDefaultCircle size={14} color={"success"} className={"additional-icons"} />
                </Grid>
            )}
            {archiveStatus === ARCHIVED_ASSET_STATUS.ARCHIVED && (
                <AssetStatusArchived
                    thumbnailSize={width === 40 ? "small" : "normal"}
                    variant={variant}
                    containerType={"archive"}
                    status={archiveStatus}
                />
            )}
            {(createdAt || created?.at) && countDaysBetweentDates({ fromDate: createdAt ?? created?.at }) < 7 && (
                <Box
                    sx={{
                        position: "absolute",
                        right: variant === "table" ? theme.spacing(width > 40 ? -0.25 : -0.75) : theme.spacing(1),
                        bottom: variant === "table" ? theme.spacing(width > 40 ? -0.25 : -0.5) : theme.spacing(1),
                        color: theme.palette.error.main,
                        zIndex: 1,
                    }}
                >
                    <IconNew
                        size={34}
                        viewBox={"0 0 34 16"}
                        style={{ height: "16px" }}
                        className={"additional-icons"}
                    />
                </Box>
            )}
            {renderMainContent()}
            {isPreviewOpen && asset && <ImagePreviewV2 onClickClose={handlePreviewClose} asset={asset} src={src} />}
        </StyledAssetTypeThumbnail>
    );
};

export default AssetThumbnail;
